// Registro de Compras (2026-10-19): la cuadratura del mes entre lo que el SII tiene registrado como
// compra de la empresa (RCV), lo que llegó por XML al correo de intercambio y lo registrado en Compras;
// y la respuesta a cada factura (acuse de recibo o reclamo, plazo de 8 días). El motor SII del ERP lee
// el RCV y envía las respuestas solo; mientras la conexión al SII real esté APAGADA, el RCV se carga con
// el CSV que el SII deja descargar y las respuestas quedan «por enviar».
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';

interface FilaCuadratura {
  rutEmisor: string;
  razonSocial: string;
  tipoSii: number;
  folio: number;
  fechaEmision: string;
  rcv: {
    total: number;
    estado: string;
    fechaRecepcion: string | null;
    fechaAcuse: string | null;
  } | null;
  xml: { id: string; estado: string; total: number } | null;
  compra: { documentoId: string; estado: string; total: number } | null;
  diasParaReclamar: number | null;
  respuestas: Array<{ id: string; accion: string; estado: string; motivo: string | null }>;
  observaciones: string[];
}

const TD: Record<number, string> = { 33: 'FCC', 34: 'FCE', 46: 'FCO', 56: 'NDC', 61: 'NCC' };
const ACCION: Record<string, string> = {
  ACD: 'Acepta contenido',
  ERM: 'Recibo de mercaderías',
  RCD: 'Reclamo al contenido',
  RFP: 'Falta parcial',
  RFT: 'Falta total',
};
const ESTADO_RESP: Record<string, string> = {
  POR_ENVIAR: 'por enviar',
  ENVIADA: 'enviada',
  RECHAZADA_SII: 'rechazada por el SII',
};
const mesActual = () => new Date().toISOString().slice(0, 7);

export function RegistroCompras({
  alAbrirFactura,
  alAbrirRecibido,
}: {
  alAbrirFactura?: (documentoId: string) => void;
  alAbrirRecibido?: (id: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [mes, setMes] = useState(mesActual());
  const periodo = mes.replace('-', '');
  const [reclamo, setReclamo] = useState<FilaCuadratura | null>(null);
  const [tipoReclamo, setTipoReclamo] = useState<'RCD' | 'RFP' | 'RFT'>('RCD');
  const [motivo, setMotivo] = useState('');
  const [soloProblemas, setSoloProblemas] = useState(false);
  const clave = ['emp', empresaId, 'rcv', periodo];
  const estado = useQuery({
    queryKey: ['rcv-estado'],
    queryFn: () => api.get<{ siiEncendido: boolean }>('/compras/rcv/estado'),
  });
  const q = useQuery({
    queryKey: clave,
    queryFn: () =>
      api.get<FilaCuadratura[]>('/compras/rcv/cuadratura', { query: { empresaId, periodo } }),
  });
  const refrescar = () => qc.invalidateQueries({ queryKey: clave });
  const archivo = useMutation({
    mutationFn: async (f: File) =>
      api.post<{ documentos: number }>(
        '/compras/rcv/archivo',
        { empresaId, csv: await f.text() },
        { idempotencia: intentos.clave('rcv', { n: f.name, t: f.size, m: f.lastModified }) },
      ),
    onSuccess: async () => {
      intentos.cerrar();
      await refrescar();
    },
    onError: (e) => intentos.cerrar(e),
  });
  const leerSii = useMutation({
    mutationFn: () =>
      api.post(
        '/compras/rcv/leer-sii',
        { empresaId, periodo },
        {
          idempotencia: intentos.clave('rcv-sii', { periodo, t: Date.now() }),
        },
      ),
    onSuccess: refrescar,
  });
  const responder = useMutation({
    mutationFn: async (x: { f: FilaCuadratura; acciones: string[]; motivo?: string }) => {
      for (const accion of x.acciones)
        await api.post(
          '/compras/rcv/respuestas',
          {
            empresaId,
            rutEmisor: x.f.rutEmisor,
            tipoSii: x.f.tipoSii,
            folio: x.f.folio,
            accion,
            motivo: x.motivo ?? null,
          },
          { idempotencia: intentos.clave('resp', { ...x.f, accion }) },
        );
    },
    onSuccess: async () => {
      setReclamo(null);
      setMotivo('');
      await refrescar();
    },
  });

  const filas = (q.data ?? []).filter((f) => !soloProblemas || f.observaciones.length);
  const cuenta = (p: (f: FilaCuadratura) => boolean) => (q.data ?? []).filter(p).length;
  const encendido = estado.data?.siiEncendido ?? false;
  const responde = puede('compras.factura.aceptar');

  return (
    <div className="pantalla consulta registro-compras" data-pantalla="registro-compras">
      <header className="encabezado-entidad">
        <span className="ruta-migas">Compras › Registro de Compras</span>
        <h2>Registro de Compras (SII)</h2>
      </header>
      <p className={`aviso-linea ${encendido ? 'tono-bueno' : 'tono-aviso'}`}>
        {encendido
          ? 'Conectado al SII: el Registro de Compras se lee solo y las respuestas se envían solas.'
          : 'Conexión al SII APAGADA (hasta el cutover): carga el CSV del Registro de Compras que bajas del SII; las respuestas quedan «por enviar» y saldrán solas cuando se encienda.'}
      </p>
      <section className="paso-cobro" aria-label="Período">
        <div className="barra-filtros">
          <label className="campo">
            <span className="etiqueta">Mes</span>
            <input
              type="month"
              aria-label="Mes"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
            />
          </label>
          <label className="boton-archivo">
            Cargar CSV del SII
            <input
              type="file"
              accept=".csv,text/csv"
              aria-label="Cargar CSV del SII"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) archivo.mutate(f);
                e.target.value = '';
              }}
            />
          </label>
          <button
            type="button"
            disabled={!encendido || leerSii.isPending}
            title={encendido ? 'Trae el Registro de Compras del mes desde el SII' : 'SII apagado'}
            onClick={() => leerSii.mutate()}
          >
            Leer del SII
          </button>
          <label className="opcion-radio">
            <input
              type="checkbox"
              checked={soloProblemas}
              onChange={(e) => setSoloProblemas(e.target.checked)}
            />
            Solo lo que falta o no cuadra
          </label>
          <span className="espaciador" />
          <div className="cifras-cliente">
            <span className="cifra">
              <span className="etiqueta">En el SII</span>
              <b>{cuenta((f) => !!f.rcv)}</b>
            </span>
            <span className={`cifra ${cuenta((f) => !f.xml) ? 'mala' : ''}`}>
              <span className="etiqueta">Sin XML</span>
              <b>{cuenta((f) => !f.xml)}</b>
            </span>
            <span className={`cifra ${cuenta((f) => !f.compra) ? 'mala' : ''}`}>
              <span className="etiqueta">Sin registrar</span>
              <b>{cuenta((f) => !f.compra)}</b>
            </span>
            <span
              className={`cifra ${cuenta((f) => f.observaciones.includes('Los montos no cuadran')) ? 'mala' : ''}`}
            >
              <span className="etiqueta">No cuadran</span>
              <b>{cuenta((f) => f.observaciones.includes('Los montos no cuadran'))}</b>
            </span>
          </div>
        </div>
        {archivo.isSuccess && (
          <p className="tono-bueno">Cargados {archivo.data.documentos} documentos del SII.</p>
        )}
        {archivo.isError && <PanelError error={archivo.error} />}
        {leerSii.isError && <PanelError error={leerSii.error} />}
      </section>
      {q.isError && <PanelError error={q.error} />}
      <table className="grilla compacta" aria-label="Registro de Compras">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>TD</th>
            <th>Folio</th>
            <th>Proveedor</th>
            <th className="der">En el SII</th>
            <th>XML</th>
            <th>En Compras</th>
            <th className="der">Días para reclamar</th>
            <th>Respuesta</th>
            <th>Observación</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            const vivas = f.respuestas;
            const tiene = (a: string) => vivas.some((r) => r.accion === a);
            const reclamada = vivas.some((r) => ['RCD', 'RFP', 'RFT'].includes(r.accion));
            return (
              <tr
                key={`${f.rutEmisor}-${f.tipoSii}-${f.folio}`}
                className={f.observaciones.length ? 'con-problema' : ''}
              >
                <td>{fecha(f.fechaEmision)}</td>
                <td>{TD[f.tipoSii] ?? f.tipoSii}</td>
                <td className="mono">{f.folio}</td>
                <td>{f.razonSocial}</td>
                <td className="der">{f.rcv ? pesos(String(f.rcv.total)) : '—'}</td>
                <td>
                  {f.xml ? (
                    <button
                      type="button"
                      className="enlace"
                      onClick={() => alAbrirRecibido?.(f.xml!.id)}
                    >
                      {f.xml.estado === 'PENDIENTE' ? 'llegó, por ingresar' : 'llegó'}
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {f.compra ? (
                    <button
                      type="button"
                      className="enlace"
                      onClick={() => alAbrirFactura?.(f.compra!.documentoId)}
                    >
                      {f.compra.estado.toLowerCase()} · {pesos(String(f.compra.total))}
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
                <td
                  className={`der ${f.diasParaReclamar !== null && f.diasParaReclamar <= 2 ? 'tono-malo' : ''}`}
                >
                  {f.diasParaReclamar ?? ''}
                </td>
                <td>
                  {vivas.map((r) => (
                    <div key={r.id} title={r.motivo ?? undefined}>
                      {ACCION[r.accion]}{' '}
                      <span className="apagado">({ESTADO_RESP[r.estado] ?? r.estado})</span>
                    </div>
                  ))}
                </td>
                <td className="apagado">{f.observaciones.join(' · ')}</td>
                <td className="acciones-fila">
                  {responde && !reclamada && !(tiene('ACD') && tiene('ERM')) && (
                    <button
                      type="button"
                      className="boton-mini primario"
                      disabled={responder.isPending}
                      title="Acepta el contenido y da recibo de las mercaderías"
                      onClick={() =>
                        responder.mutate({
                          f,
                          acciones: ['ACD', 'ERM'].filter((a) => !tiene(a)),
                        })
                      }
                    >
                      Aceptar
                    </button>
                  )}
                  {responde && !reclamada && !tiene('ACD') && (
                    <button type="button" className="boton-mini" onClick={() => setReclamo(f)}>
                      Reclamar
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
          {q.isSuccess && !filas.length && (
            <tr>
              <td colSpan={11} className="apagado">
                Sin documentos en el mes. Carga el CSV del Registro de Compras del SII.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {responder.isError && <PanelError error={responder.error} />}
      {reclamo && (
        <Modal
          titulo={`Reclamar ${TD[reclamo.tipoSii] ?? ''} ${reclamo.folio}`}
          alCerrar={() => setReclamo(null)}
        >
          <label className="campo">
            <span className="etiqueta">Tipo de reclamo</span>
            <select
              aria-label="Tipo de reclamo"
              value={tipoReclamo}
              onChange={(e) => setTipoReclamo(e.target.value as typeof tipoReclamo)}
            >
              <option value="RCD">Reclamo al contenido</option>
              <option value="RFP">Falta parcial de mercaderías</option>
              <option value="RFT">Falta total de mercaderías</option>
            </select>
          </label>
          <label className="campo">
            <span className="etiqueta">Motivo</span>
            <input
              aria-label="Motivo del reclamo"
              autoFocus
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          <p className="apagado">
            Un reclamo enviado al SII es oficial y no se puede deshacer. Con el SII apagado queda
            por enviar.
          </p>
          <div className="acciones-modal">
            <button type="button" onClick={() => setReclamo(null)}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={motivo.trim().length < 5 || responder.isPending}
              onClick={() => responder.mutate({ f: reclamo, acciones: [tipoReclamo], motivo })}
            >
              Reclamar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
